/**
 * Booking detail for either side of the booking.
 *
 * `side="user"` reads GET /user/bookings/:id and shows the vendor as the
 * counterparty; `side="vendor"` reads GET /vendor/bookings/:id and shows the
 * customer instead. Everything else — service, pet, plan/course context
 * ("session N of M"), walk-report summary, incidents — is shared.
 */
import { useEffect } from 'react';
import { CalendarClock, Loader2, MapPin, Phone, Repeat, Stethoscope, TriangleAlert, User, Users, X } from 'lucide-react';
import { use_booking_detail, use_vendor_booking_detail } from '@/features/booking/hooks';
import { BookingPaymentBreakdownPanel } from '@/components/booking-payment-breakdown';
import { slot_label, slot_range_label } from '@/lib/datetime';
import { BookingPetCard } from '@/components/booking-pet-card';
import { PendingPaymentCallout } from '@/components/pending-payment-callout';

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return slot_label(iso);
}

const STATUS_STYLE: Record<string, string> = {
  PENDING_PAYMENT: 'bg-muted text-muted-foreground',
  AWAITING_VENDOR: 'bg-warning/15 text-warning',
  CONFIRMED: 'bg-info/12 text-info',
  IN_PROGRESS: 'bg-primary/12 text-primary',
  COMPLETED: 'bg-success/12 text-success',
  CANCELLED: 'bg-destructive/12 text-destructive',
  NO_SHOW: 'bg-destructive/12 text-destructive',
};

export function BookingDetailModal({
  booking_id,
  on_close,
  side = 'user',
}: {
  booking_id: string;
  on_close: () => void;
  side?: 'user' | 'vendor';
}) {
  const is_vendor = side === 'vendor';
  // Both hooks are called every render (rules of hooks); only the active side is
  // enabled, so exactly one request goes out.
  const user_q = use_booking_detail(is_vendor ? null : booking_id);
  const vendor_q = use_vendor_booking_detail(is_vendor ? booking_id : null);
  const detail_q = is_vendor ? vendor_q : user_q;
  const b = detail_q.data ?? null;

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="booking-detail-modal"
      onClick={on_close}
    >
      <div className="my-8 w-full max-w-lg rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
            <CalendarClock className="h-5 w-5 text-primary" /> Booking details
          </h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted" data-testid="booking-detail-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {detail_q.isLoading ? (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !b ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="booking-detail-empty">
            Couldn't load this booking.
          </p>
        ) : (
          <div className="flex flex-col gap-4 p-5 text-sm">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-semibold">{b.service?.name ?? b.module_type}</p>
                {/*
                  The full window, not just the start. A start time alone cannot be checked
                  against the other party's screen, so a disagreement about when a booking is
                  survives both people looking straight at it.
                */}
                <p className="text-xs text-muted-foreground" data-testid="booking-detail-slot">
                  {b.slot_end ? slot_range_label(b.slot_start, b.slot_end) : fmt(b.slot_start)}
                </p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[b.status] ?? 'bg-muted text-muted-foreground'}`}>
                {b.status.replace(/_/g, ' ').toLowerCase()}
              </span>
            </div>

            {b.plan_context && (
              <div className="flex items-center gap-2 rounded-input bg-secondary/40 p-2 text-xs" data-testid="booking-detail-plan-context">
                <Repeat className="h-4 w-4 shrink-0 text-primary" />
                <span>
                  {b.plan_context.kind === 'WALK_PLAN' ? 'Walk plan' : 'Training package'} — session{' '}
                  <span className="font-semibold">{b.plan_context.session_number}</span> of {b.plan_context.total_sessions}
                  <span className="text-muted-foreground"> · {b.plan_context.status.toLowerCase()}</span>
                </span>
              </div>
            )}

            {b.vendor && (
              <Row icon={<MapPin className="h-4 w-4" />} label="Provider">
                {b.vendor.business_name}
                {b.vendor.city ? `, ${b.vendor.city}` : ''}
                {b.vendor.business_phone && (
                  <span className="ml-1 inline-flex items-center gap-1 text-muted-foreground">
                    <Phone className="h-3 w-3" /> {b.vendor.business_phone}
                  </span>
                )}
              </Row>
            )}
            {b.customer && (
              <Row icon={<User className="h-4 w-4" />} label="Customer">
                {b.customer.name ?? 'Pet owner'}
                {b.customer.phone && (
                  <span className="ml-1 inline-flex items-center gap-1 text-muted-foreground">
                    <Phone className="h-3 w-3" /> {b.customer.phone}
                  </span>
                )}
              </Row>
            )}
            {/*
              The way to finish an abandoned payment, on the screen QA and customers actually
              open. It was on the My Bookings list only, so someone who came back from Google
              Pay without paying, tapped into the booking, and looked for a pay button found a
              status pill and nothing else.

              Owner side only — a vendor must never be able to pay off a customer's booking.
            */}
            {!is_vendor && b.status === 'PENDING_PAYMENT' && (
              <PendingPaymentCallout booking={b} service_name={b.service?.name ?? 'Booking'} />
            )}
            {b.pet && <BookingPetCard pet={b.pet} />}
            {/* A restaurant was being asked to accept a table reservation without being told how
                many people were coming — the API carried party_size and no vendor surface showed
                it (BUG-082). It is also what made the capacity overselling in BUG-081 invisible
                from the vendor's side. */}
            {b.party_size ? (
              <Row icon={<Users className="h-4 w-4" />} label="Party size">
                {b.party_size} {b.party_size === 1 ? 'guest' : 'guests'}
              </Row>
            ) : null}
            {b.pickup_address && <Row icon={<MapPin className="h-4 w-4" />} label="Pickup">{b.pickup_address}</Row>}

            {b.walk_report && (
              <div className="rounded-input border border-border p-3" data-testid="booking-detail-walk-report">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Stethoscope className="h-3.5 w-3.5" /> Walk report
                  <span className="ml-auto normal-case">{b.walk_report.status.toLowerCase()}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {fmt(b.walk_report.actual_start)} → {fmt(b.walk_report.actual_end)}
                  {b.walk_report.distance_km ? ` · ${b.walk_report.distance_km} km` : ''}
                </p>
                {(b.walk_report.potty_pee || b.walk_report.potty_poop) && (
                  <p className="text-xs text-muted-foreground">
                    Potty: {[b.walk_report.potty_pee ? 'pee' : null, b.walk_report.potty_poop ? 'poop' : null].filter(Boolean).join(', ')}
                  </p>
                )}
                {b.walk_report.notes && <p className="mt-1 text-xs">{b.walk_report.notes}</p>}
              </div>
            )}

            {(b.incidents?.length ?? 0) > 0 && (
              <div className="rounded-input border border-border p-3" data-testid="booking-detail-incidents">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <TriangleAlert className="h-3.5 w-3.5" /> Incidents ({b.incidents!.length})
                </p>
                <ul className="flex flex-col gap-1.5">
                  {b.incidents!.map((i) => (
                    <li key={i.id} className="text-xs">
                      <span className="font-medium">{i.type.toLowerCase()}</span>
                      <span className="text-muted-foreground"> · {i.severity.toLowerCase()} · {i.status.toLowerCase()}</span>
                      {i.note && <span className="block text-muted-foreground">{i.note}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/*
              * The vendor gets the full split — customer paid, our fee, their
              * take-home — because on a service the fee is deducted and the booking
              * price alone would overstate what they are owed. The field is absent
              * from the customer payload, so the panel renders for nobody else and
              * the plain price line below stays their view.
              */}
            {b.payment_breakdown ? (
              <BookingPaymentBreakdownPanel
                breakdown={b.payment_breakdown}
                payment_status={b.payment?.status}
              />
            ) : (
              b.payment && (
                <p className="text-xs text-muted-foreground">
                  ₹{b.price_inr} · {b.payment.status === 'CAPTURED' ? 'Paid' : b.payment.status.toLowerCase()}
                </p>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p>{children}</p>
      </div>
    </div>
  );
}
